import { Injectable } from '@angular/core';
import { differsAtCentPrecision } from './validation-field-key.util';

/**
 * Configuration for column sum validation
 * Used by validateColumnSum() to define which columns to validate
 */
export interface ColumnSumValidationConfig {
  /** The qname of the sub-total/sum column to validate */
  subTotalColumnQname: string;
  /** Array of component column qnames that should sum to the sub-total */
  componentColumnQnames: string[];
  /** Label for the sub-total column (used in error messages) */
  subTotalLabel: string;
  /** Labels for the component columns (used in error messages, in same order as componentColumnQnames) */
  componentLabels: string[];
  /**
   * Optional allow-list of row qnames this sub-total check applies to. When
   * present and non-empty, the validation runs ONLY for these rows; when
   * absent, it runs for every row in `rowQnames` (legacy default). Lets a
   * schema scope a sub-total to specific equity-movement rows — e.g. the
   * non-distributable reserves sub-total excludes the Acquisition-of-subsidiary
   * row (both taxonomies) and the Total-increase-in-equity row (MPERS only).
   * Mirrored by the headless Role19 validator (Driver #6) to keep the mounted
   * editor and conversion status in parity.
   */
  appliesToRowQnames?: string[];
}

/**
 * ColumnSumValidationService
 *
 * Validates that a sub-total column equals the sum of its component columns,
 * per row, across the requested consolidation/period tables. Relocated verbatim
 * from the legacy combined validation-warning service as part of the
 * validation-warning split.
 */
@Injectable({
  providedIn: 'root'
})
export class ColumnSumValidationService {
  /**
   * Validate that a sub-total column equals the sum of its component columns
   *
   * This is a generic validation that can be used for any "sub-total = sum of components" scenario.
   * Examples:
   * - MPERS: Sub-total of non-distributable reserves = Capital reserve + Foreign currency translation reserve + Revaluation surplus + Other non-distributable reserves
   * - MFRS: Similar validations with different qnames
   * - Any other sheet with similar sum validation requirements
   *
   * This validation is per row (each line item is treated separately)
   *
   * @param formValues - Raw form values from the form
   * @param rowQnames - Array of row qnames to validate (non-header controls with equityColumns)
   * @param consolidationTypes - Array of consolidation types to check (e.g., ['Consolidated', 'Separate'])
   * @param periodTypes - Array of period types to check (e.g., ['Current', 'Previous'])
   * @param getControlName - Function to generate control name from row qname, column qname, consolidation type, and period type
   * @param config - Configuration object containing qnames and labels for the validation
   * @returns Object with cellErrors (Map of control name to error message) and rowErrors (Map of row qname to array of error messages)
   */
  validateColumnSum(
    formValues: any,
    rowQnames: string[],
    consolidationTypes: string[],
    periodTypes: string[],
    getControlName: (rowQname: string, columnQname: string, consolidationType: string, periodType: string) => string,
    config: ColumnSumValidationConfig
  ): {
    cellErrors: Map<string, string>;
    rowErrors: Map<string, string[]>;
  } {
    const cellErrors = new Map<string, string>();
    const rowErrors = new Map<string, string[]>();

    const { subTotalColumnQname, componentColumnQnames, subTotalLabel, componentLabels, appliesToRowQnames } = config;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 [validateColumnSum] Validating: ${subTotalLabel} (${subTotalColumnQname})`);
    // console.log(`🔍 [validateColumnSum] Component columns:`, componentColumnQnames);

    // Honour the config's optional row allow-list: present & non-empty → only
    // those rows; absent → every row passed in. Mirrored by the headless Role19
    // validator (checkColumnSumErrors) so the editor and conversion status agree.
    const scopedRowQnames =
      appliesToRowQnames && appliesToRowQnames.length > 0
        ? rowQnames.filter(rowQname => appliesToRowQnames.includes(rowQname))
        : rowQnames;

    // Check each row
    scopedRowQnames.forEach(rowQname => {
      const errors: string[] = [];

      consolidationTypes.forEach(consolidationType => {
        periodTypes.forEach(periodType => {
          // Calculate expected sum from component columns
          let expectedSum = 0;
          const componentValues: Record<string, number> = {};
          componentColumnQnames.forEach(componentQname => {
            const componentControlName = getControlName(rowQname, componentQname, consolidationType, periodType);
            const rawValue = formValues[componentControlName];
            const value = parseFloat(rawValue) || 0;
            componentValues[componentQname] = value;
            expectedSum += value;
          });

          // Get actual value in sub-total column
          const subTotalControlName = getControlName(rowQname, subTotalColumnQname, consolidationType, periodType);
          const rawSubTotalValue = formValues[subTotalControlName];
          const actualValue = parseFloat(rawSubTotalValue) || 0;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // Logging 3 specific subtotal columns
          // if (subTotalColumnQname.includes('NonDistributableReserves') ||
          //     subTotalColumnQname.includes('OtherReserves') ||
          //     subTotalColumnQname.includes('EquityAttributableToOwners')) {
          //   console.log(`🔍 [validateColumnSum] DEBUG for ${subTotalLabel}:`);
          //   console.log(`   Row: ${rowQname}, Table: [${consolidationType}][${periodType}]`);
          //   console.log(`   SubTotal controlName: ${subTotalControlName}`);
          //   console.log(`   SubTotal key exists in formValues: ${subTotalControlName in formValues}`);
          //   console.log(`   SubTotal rawValue: ${rawSubTotalValue}, actualValue: ${actualValue}`);
          //   console.log(`   Component values:`, componentValues);
          //   console.log(`   Expected sum: ${expectedSum}`);
          //   console.log(`   Diff: ${Math.abs(actualValue - expectedSum)}`);
          //   console.log(`   Will add error: ${Math.abs(actualValue - expectedSum) > 0.01}`);
          // }

          // Round to cents, flag any difference (≥ 0.01): a sub-total that is
          // one cent off its component sum must be reported (raw `> 0.01` both
          // missed a one-cent diff and skipped rounding).
          if (differsAtCentPrecision(actualValue, expectedSum)) {
            // Build error message with component labels
            const componentList = componentLabels.map(label => `- ${label}`).join('\n');
            const label = `${subTotalLabel} [${consolidationType} ${periodType}]`;
            errors.push(
              `${label}: ${subTotalLabel} should equal to the sum of:\n${componentList}`
            );

            // Store cell-level error for red border
            cellErrors.set(subTotalControlName, `Value does not match expected sum (${expectedSum.toFixed(2)})`);

            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`❌ [validateColumnSum] MISMATCH DETECTED for ${subTotalLabel} in row ${rowQname}`);
          }
        });
      });

      if (errors.length > 0) {
        rowErrors.set(rowQname, errors);
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 [validateColumnSum] Result for ${subTotalLabel}: cellErrors=${cellErrors.size}, rowErrors=${rowErrors.size}`);

    return { cellErrors, rowErrors };
  }

  /**
   * Check if a specific cell has a column sum validation error
   *
   * @param cellErrors - Map of cell errors from validateColumnSum
   * @param controlName - The control name to check
   * @returns true if the cell has an error
   */
  hasColumnSumError(
    cellErrors: Map<string, string>,
    controlName: string
  ): boolean {
    return cellErrors.has(controlName);
  }
}
